import { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Calendar, ChevronLeft, ChevronRight, Clock, Phone, 
  MessageCircle, User, X, Plus, Bell, AlertCircle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Theme colors
const theme = {
  bg: '#F7F4EF',
  ink: '#1A1713',
  ink2: '#3D3830',
  ink3: '#6B6358',
  orange: '#E8621A',
  green: '#059669',
  blue: '#3B82F6',
  purple: '#8B5CF6',
  red: '#DC2626',
};

// WhatsApp number
const WHATSAPP_NUMBER = "33745522450";

// Format date for display
const formatDate = (date) => {
  return new Date(date).toLocaleDateString('fr-FR', {
    weekday: 'short',
    day: 'numeric',
    month: 'short'
  });
};

const formatTime = (date) => {
  return new Date(date).toLocaleTimeString('fr-FR', {
    hour: '2-digit',
    minute: '2-digit'
  });
};

// Calendar View Component
export const CalendarView = ({ leads, onSelectLead, onScheduleCall, onRefresh }) => {
  const [viewMode, setViewMode] = useState('week'); // 'day' | 'week'
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  
  // Get scheduled calls from leads
  const scheduledCalls = useMemo(() => {
    return leads
      .filter(lead => lead.scheduled_call)
      .map(lead => ({
        ...lead,
        callDate: new Date(lead.scheduled_call.datetime),
        callTime: formatTime(lead.scheduled_call.datetime)
      }))
      .sort((a, b) => a.callDate - b.callDate);
  }, [leads]);
  
  // Get week days
  const getWeekDays = (date) => {
    const start = new Date(date);
    start.setDate(start.getDate() - start.getDay() + 1); // Start on Monday
    const days = [];
    for (let i = 0; i < 7; i++) {
      const day = new Date(start);
      day.setDate(start.getDate() + i);
      days.push(day);
    }
    return days;
  };
  
  const weekDays = getWeekDays(currentDate);
  
  // Get calls for a specific day
  const getCallsForDay = (date) => {
    return scheduledCalls.filter(call => {
      const callDay = new Date(call.callDate);
      return callDay.toDateString() === date.toDateString();
    });
  };
  
  // Navigation
  const goToPrevious = () => {
    const newDate = new Date(currentDate);
    if (viewMode === 'week') {
      newDate.setDate(newDate.getDate() - 7);
    } else {
      newDate.setDate(newDate.getDate() - 1);
    }
    setCurrentDate(newDate);
  };
  
  const goToNext = () => {
    const newDate = new Date(currentDate);
    if (viewMode === 'week') {
      newDate.setDate(newDate.getDate() + 7);
    } else {
      newDate.setDate(newDate.getDate() + 1);
    }
    setCurrentDate(newDate);
  };
  
  const goToToday = () => {
    setCurrentDate(new Date());
    setSelectedDate(new Date());
  };
  
  // Check if date is today
  const isToday = (date) => {
    return date.toDateString() === new Date().toDateString();
  };
  
  // Check if date is selected
  const isSelected = (date) => {
    return date.toDateString() === selectedDate.toDateString();
  };
  
  // Time slots for day view
  const timeSlots = Array.from({ length: 12 }, (_, i) => i + 8); // 8:00 to 19:00
  
  // Get calls for selected day (day view)
  const selectedDayCalls = getCallsForDay(selectedDate);
  
  // Upcoming calls (next 24h)
  const upcomingCalls = useMemo(() => {
    const now = new Date();
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    return scheduledCalls.filter(call => 
      call.callDate >= now && call.callDate <= tomorrow
    );
  }, [scheduledCalls]);

  return (
    <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-gray-100">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Calendar className="w-6 h-6 text-purple-600" />
            <h2 className="text-xl font-bold text-gray-800">Calendrier des appels</h2>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={goToToday}
              className="px-3 py-1.5 text-sm font-bold text-purple-600 bg-purple-50 rounded-lg hover:bg-purple-100 transition-colors"
            >
              Aujourd'hui
            </button>
            <button
              onClick={goToPrevious}
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <ChevronLeft className="w-5 h-5 text-gray-600" />
            </button>
            <button
              onClick={goToNext}
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <ChevronRight className="w-5 h-5 text-gray-600" />
            </button>
          </div>
        </div>
        
        {/* View toggle */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => setViewMode('day')}
            className={`px-4 py-2 text-sm font-bold rounded-lg transition-colors ${
              viewMode === 'day' 
                ? 'bg-purple-600 text-white' 
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            Jour
          </button>
          <button
            onClick={() => setViewMode('week')}
            className={`px-4 py-2 text-sm font-bold rounded-lg transition-colors ${
              viewMode === 'week' 
                ? 'bg-purple-600 text-white' 
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            Semaine
          </button>
          
          {/* Current period display */}
          <span className="ml-4 text-sm font-medium text-gray-600">
            {viewMode === 'week' 
              ? `${formatDate(weekDays[0])} - ${formatDate(weekDays[6])}`
              : formatDate(selectedDate)
            }
          </span>
        </div>
      </div>
      
      {/* Upcoming calls alert */}
      {upcomingCalls.length > 0 && (
        <div className="p-3 bg-amber-50 border-b border-amber-100">
          <div className="flex items-center gap-2">
            <Bell className="w-4 h-4 text-amber-600" />
            <span className="text-sm font-bold text-amber-700">
              {upcomingCalls.length} appel{upcomingCalls.length > 1 ? 's' : ''} dans les 24h
            </span>
          </div>
          <div className="mt-2 space-y-1">
            {upcomingCalls.slice(0, 3).map((call, idx) => (
              <div 
                key={idx}
                className="flex items-center justify-between text-xs bg-white rounded-lg px-2 py-1.5"
              >
                <span className="font-medium text-gray-800">{call.restaurant}</span>
                <span className="font-bold text-amber-600">{call.callTime}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Week View */}
      {viewMode === 'week' && (
        <div className="p-4">
          <div className="grid grid-cols-7 gap-2">
            {weekDays.map((day, idx) => {
              const dayCalls = getCallsForDay(day);
              const dayIsToday = isToday(day);
              const dayIsSelected = isSelected(day);
              
              return (
                <div
                  key={idx}
                  onClick={() => {
                    setSelectedDate(day);
                    setViewMode('day');
                  }}
                  className={`p-3 rounded-xl border-2 cursor-pointer transition-all hover:scale-[1.02] ${
                    dayIsToday 
                      ? 'border-purple-500 bg-purple-50' 
                      : dayIsSelected
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  {/* Day header */}
                  <div className="text-center mb-2">
                    <p className="text-xs font-medium text-gray-500 uppercase">
                      {day.toLocaleDateString('fr-FR', { weekday: 'short' })}
                    </p>
                    <p className={`text-2xl font-black ${
                      dayIsToday ? 'text-purple-600' : 'text-gray-800'
                    }`}>
                      {day.getDate()}
                    </p>
                  </div>
                  
                  {/* Calls for this day */}
                  <div className="space-y-1.5 min-h-[80px]">
                    {dayCalls.length === 0 ? (
                      <p className="text-[10px] text-gray-400 text-center py-4">Aucun appel</p>
                    ) : (
                      dayCalls.slice(0, 3).map((call, callIdx) => (
                        <div
                          key={callIdx}
                          onClick={(e) => {
                            e.stopPropagation();
                            onSelectLead && onSelectLead(call);
                          }}
                          className="px-2 py-1.5 rounded-lg text-xs font-medium truncate"
                          style={{ 
                            backgroundColor: `${theme.purple}15`,
                            color: theme.purple,
                            border: `1px solid ${theme.purple}30`
                          }}
                        >
                          <div className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            <span>{call.callTime}</span>
                          </div>
                          <p className="truncate mt-0.5 font-bold">{call.restaurant}</p>
                        </div>
                      ))
                    )}
                    {dayCalls.length > 3 && (
                      <p className="text-[10px] text-center text-purple-600 font-bold">
                        +{dayCalls.length - 3} autres
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
      
      {/* Day View */}
      {viewMode === 'day' && (
        <div className="p-4">
          <div className="mb-4">
            <h3 className="text-lg font-bold text-gray-800">
              {selectedDate.toLocaleDateString('fr-FR', { 
                weekday: 'long', 
                day: 'numeric', 
                month: 'long' 
              })}
            </h3>
            <p className="text-sm text-gray-500">
              {selectedDayCalls.length} appel{selectedDayCalls.length !== 1 ? 's' : ''} programmé{selectedDayCalls.length !== 1 ? 's' : ''}
            </p>
          </div>
          
          {/* Time slots */}
          <div className="space-y-2 max-h-[400px] overflow-y-auto">
            {timeSlots.map((hour) => {
              const slotCalls = selectedDayCalls.filter(call => 
                call.callDate.getHours() === hour
              );
              
              return (
                <div 
                  key={hour} 
                  className="flex gap-3 py-2 border-b border-gray-100"
                >
                  {/* Time label */}
                  <div className="w-16 flex-shrink-0">
                    <span className="text-sm font-bold text-gray-400">
                      {hour}:00
                    </span>
                  </div>
                  
                  {/* Calls in this slot */}
                  <div className="flex-1 space-y-2">
                    {slotCalls.length === 0 ? (
                      <div className="h-10 border-2 border-dashed border-gray-200 rounded-lg" />
                    ) : (
                      slotCalls.map((call, idx) => (
                        <div
                          key={idx}
                          onClick={() => onSelectLead && onSelectLead(call)}
                          className="flex items-center justify-between p-3 rounded-xl cursor-pointer hover:scale-[1.01] transition-all"
                          style={{ 
                            backgroundColor: `${theme.purple}10`,
                            border: `2px solid ${theme.purple}30`
                          }}
                        >
                          <div className="flex items-center gap-3">
                            <div 
                              className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold"
                              style={{ backgroundColor: theme.purple }}
                            >
                              {call.restaurant?.charAt(0) || '?'}
                            </div>
                            <div>
                              <p className="font-bold text-gray-800">{call.restaurant}</p>
                              <p className="text-xs text-gray-500">{call.owner || 'Contact'}</p>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-bold text-purple-600">
                              {call.callTime}
                            </span>
                            {call.phone && (
                              <a
                                href={`https://wa.me/${call.phone.replace(/[^0-9]/g, '')}?text=${encodeURIComponent(`Salut ! C'est l'heure de notre appel pour ${call.restaurant}. Je t'appelle ?`)}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                className="p-2 rounded-lg bg-green-500 text-white hover:bg-green-600 transition-colors"
                              >
                                <MessageCircle className="w-4 h-4" />
                              </a>
                            )}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
      
      {/* Stats footer */}
      <div className="p-4 bg-gray-50 border-t border-gray-100">
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-500">
            📅 {scheduledCalls.length} appel{scheduledCalls.length !== 1 ? 's' : ''} total
          </span>
          <span className="text-gray-500">
            📆 Cette semaine: {weekDays.reduce((acc, day) => acc + getCallsForDay(day).length, 0)}
          </span>
        </div>
      </div>
    </div>
  );
};

// Schedule Call Modal Component
export const ScheduleCallModal = ({ lead, isOpen, onClose, onSchedule }) => {
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Generate time slots
  const timeSlots = [];
  for (let h = 8; h <= 19; h++) {
    timeSlots.push(`${h.toString().padStart(2, '0')}:00`);
    timeSlots.push(`${h.toString().padStart(2, '0')}:30`);
  }
  
  // Set default date to tomorrow
  useEffect(() => {
    if (isOpen) {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      setSelectedDate(tomorrow.toISOString().split('T')[0]);
      setSelectedTime('10:00');
      setNotes('');
    }
  }, [isOpen]);
  
  const handleSubmit = async () => {
    if (!selectedDate || !selectedTime) return;
    
    setIsSubmitting(true);
    try {
      const datetime = new Date(`${selectedDate}T${selectedTime}`);
      await onSchedule(lead.id, datetime.toISOString(), notes);
      onClose();
    } catch (error) {
      console.error('Failed to schedule call:', error);
      alert('Erreur lors de la programmation');
    }
    setIsSubmitting(false);
  };
  
  if (!isOpen) return null;
  
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
        >
          {/* Header */}
          <div className="p-4 bg-purple-600 text-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                <h3 className="text-lg font-bold">Programmer un appel</h3>
              </div>
              <button
                onClick={onClose}
                className="p-1 rounded-lg hover:bg-white/20 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
          
          {/* Lead info */}
          <div className="p-4 bg-purple-50 border-b border-purple-100">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-purple-600 flex items-center justify-center text-white font-bold text-lg">
                {lead?.restaurant?.charAt(0) || '?'}
              </div>
              <div>
                <p className="font-bold text-gray-800">{lead?.restaurant || 'Restaurant'}</p>
                <p className="text-sm text-gray-500">{lead?.owner || 'Contact'}</p>
              </div>
            </div>
          </div>
          
          {/* Form */}
          <div className="p-4 space-y-4">
            {/* Date picker */}
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">
                📅 Date de l'appel
              </label>
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
                className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-purple-500 focus:outline-none transition-colors"
              />
            </div>
            
            {/* Time picker */}
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">
                🕐 Heure de l'appel
              </label>
              <select
                value={selectedTime}
                onChange={(e) => setSelectedTime(e.target.value)}
                className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-purple-500 focus:outline-none transition-colors"
              >
                {timeSlots.map((time) => (
                  <option key={time} value={time}>{time}</option>
                ))}
              </select>
            </div>
            
            {/* Notes */}
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">
                📝 Notes (optionnel)
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Points à aborder, contexte..."
                rows={3}
                className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-purple-500 focus:outline-none transition-colors resize-none"
              />
            </div>
          </div>
          
          {/* Actions */}
          <div className="p-4 bg-gray-50 border-t border-gray-100 flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-3 rounded-xl text-gray-600 font-bold hover:bg-gray-200 transition-colors"
            >
              Annuler
            </button>
            <button
              onClick={handleSubmit}
              disabled={!selectedDate || !selectedTime || isSubmitting}
              className="flex-1 px-4 py-3 rounded-xl bg-purple-600 text-white font-bold hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isSubmitting ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  <Plus className="w-5 h-5" />
                  Programmer
                </>
              )}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// Call History Component
export const CallHistory = ({ calls }) => {
  if (!calls || calls.length === 0) {
    return (
      <div className="text-center py-4 text-gray-400 text-sm">
        Aucun historique d'appels
      </div>
    );
  }
  
  return (
    <div className="space-y-2">
      {calls.map((call, idx) => (
        <div 
          key={idx}
          className="flex items-center justify-between p-2 rounded-lg bg-gray-50"
        >
          <div className="flex items-center gap-2">
            {call.outcome === 'completed' ? (
              <Phone className="w-4 h-4 text-green-500" />
            ) : call.outcome === 'no_show' ? (
              <AlertCircle className="w-4 h-4 text-orange-500" />
            ) : (
              <Phone className="w-4 h-4 text-gray-400" />
            )}
            <div>
              <p className="text-xs font-medium text-gray-800">
                {new Date(call.datetime).toLocaleDateString('fr-FR', {
                  day: 'numeric',
                  month: 'short',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </p>
              {call.notes && (
                <p className="text-[10px] text-gray-500 truncate max-w-[150px]">
                  {call.notes}
                </p>
              )}
            </div>
          </div>
          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
            call.outcome === 'completed' 
              ? 'bg-green-100 text-green-700' 
              : call.outcome === 'no_show'
                ? 'bg-orange-100 text-orange-700'
                : 'bg-gray-100 text-gray-600'
          }`}>
            {call.outcome === 'completed' ? '✅ Fait' : 
             call.outcome === 'no_show' ? '👻 No show' : 
             '📅 Programmé'}
          </span>
        </div>
      ))}
    </div>
  );
};

export default CalendarView;
